import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, RefreshCw, TrendingUp } from "lucide-react";
import { rgApi, firstName } from "@/lib/rgApi";
import { platformOf } from "./PlatformLogo";

/**
 * Finances — CALENDRIER MENSUEL du suivi financier (même onglet, page unique).
 * Réutilise les données du calendrier de travail (/calendar/events) en lecture
 * seule : il n'est jamais modifié.
 *
 * Bases prévisionnelles par nuit (le GROS montant est celui compté dans la jauge) :
 *   • Airbnb  : 134,35 € (net encaissé)
 *       Voyageur : 166,95 € payés · Commission Airbnb 24,65 € (15,5 % des 159 €)
 *       Taxe de séjour déjà reversée : 7,95 €
 *   • Lodgify : 163,55 € (compté SANS redéduire la taxe)
 *       Voyageur : 168 € payés · Frais de traitement 2,77 € · Commission 1,68 €
 *       Dont 8 € de taxe de séjour à reverser (dans mes charges)
 * Expériences au jour prévu, chacune comptée UNE fois : 🥐 50 € · 🌸 70 € · 🌹 50 €.
 * Marchés et ménage exclus. Objectif mensuel : 2 600 €.
 */

const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const CASH_TARGET = 2600;

const RATE = {
  airbnb: {
    big: 134.35, guestNight: 166.95, nightValue: 159, commissionNight: 24.65, taxNight: 7.95,
    band: [["Total voyageur", 166.95], ["Commission Airbnb", 24.65], ["Taxe de séjour", 7.95]],
    lines: [
      ["Voyageur", "166,95 € payés"],
      ["Commission Airbnb", "24,65 € (15,5 % des 159 €)"],
      ["Taxe séjour reversée", "7,95 €"],
    ],
  },
  lodgify: {
    big: 163.55, guestNight: 168.0, nightValue: 160,
    band: [["Total voyageur", 168.0], ["Frais de traitement", 2.77], ["Commission plateforme", 1.68], ["Taxe de séjour à reverser (mes charges)", 8.0]],
    lines: [
      ["Voyageur", "168 € payés"],
      ["Frais de traitement", "2,77 €"],
      ["Commission plateforme", "1,68 €"],
      ["Dont taxe à reverser", "8 € (mes charges)"],
    ],
  },
};
const EXP = {
  petit_dejeuner: { icon: "🥐", price: 50, label: "Petit-déj" },
  bain_fleurs: { icon: "🌸", price: 70, label: "Bain de fleurs" },
  decor: { icon: "🌹", price: 50, label: "Décor du refuge" },
};

function pad(n) { return n < 10 ? `0${n}` : `${n}`; }
function toIso(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function ddmm(iso) { return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`; }
function eur(n) { return n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €"; }
function eur0(n) { return n.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + " €"; }

export default function Finances() {
  const [ref, setRef] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const year = ref.getFullYear();
  const month = ref.getMonth();
  const from = toIso(new Date(year, month, 1));
  const to = toIso(new Date(year, month + 1, 0));

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await rgApi.calendarEvents(from, to)); }
    catch (e) { if (e.status === 401) nav("/admin"); }
    finally { setLoading(false); }
  }, [from, to, nav]);
  useEffect(() => { load(); }, [load]);

  // Construction jour → { nights[], departures[], exps[] }
  const dayMap = useMemo(() => {
    const map = {};
    const ensure = (iso) => (map[iso] ||= { nights: [], departures: [], exps: [] });
    const resas = data?.reservations || [];
    resas.forEach((r) => {
      const platform = platformOf(r.source);
      let d = new Date(r.date_arrival + "T12:00:00");
      const dep = new Date(r.date_departure + "T12:00:00");
      while (d < dep) { ensure(toIso(d)).nights.push({ r, platform }); d.setDate(d.getDate() + 1); }
      ensure(r.date_departure).departures.push({ r, platform });
    });
    (data?.breakfasts || []).forEach((b) => { if (b.date) ensure(b.date).exps.push({ key: "petit_dejeuner", r: b.reservation_id }); });
    (data?.experiences || []).forEach((x) => { if (EXP[x.extra_key] && x.date) ensure(x.date).exps.push({ key: x.extra_key, r: x.reservation_id }); });
    return map;
  }, [data]);

  // 1ʳᵉ nuitée du séjour visible dans le mois → emplacement du bandeau unique
  const firstNightByResa = useMemo(() => {
    const out = {};
    (data?.reservations || []).forEach((r) => {
      const start = r.date_arrival >= from ? r.date_arrival : from;
      if (start >= from && start <= to && start < r.date_departure) out[r.id] = start;
    });
    return out;
  }, [data, from, to]);

  // Jauge = nuits du mois (gros montant) + expériences du mois (1 fois)
  const gauge = useMemo(() => {
    let total = 0, nightsCount = 0, expCount = 0;
    Object.entries(dayMap).forEach(([iso, cell]) => {
      if (iso < from || iso > to) return;
      cell.nights.forEach(({ platform }) => {
        const rt = RATE[platform];
        if (rt) { total += rt.big; nightsCount += 1; }
      });
      cell.exps.forEach(({ key }) => { total += EXP[key].price; expCount += 1; });
    });
    return { total, nightsCount, expCount };
  }, [dayMap, from, to]);

  const pct = Math.min(100, CASH_TARGET > 0 ? (gauge.total / CASH_TARGET) * 100 : 0);
  const reached = gauge.total >= CASH_TARGET;

  const prevMonth = () => { const d = new Date(ref); d.setMonth(d.getMonth() - 1); setRef(d); };
  const nextMonth = () => { const d = new Date(ref); d.setMonth(d.getMonth() + 1); setRef(d); };

  // Grille du mois (lundi → dimanche)
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) cells.push(day);

  return (
    <div className="rg-page" data-testid="rg-finances-page">
      <div className="rg-row" style={{ marginBottom: 12, justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div className="rg-row" style={{ gap: 8 }}>
          <button className="rg-btn ghost small" onClick={prevMonth} data-testid="rg-fin-prev"><ChevronLeft size={14} /></button>
          <h2 className="rg-h2">Suivi financier · {MONTHS[month]} {year}</h2>
          <button className="rg-btn ghost small" onClick={nextMonth} data-testid="rg-fin-next"><ChevronRight size={14} /></button>
        </div>
        <button className="rg-btn ghost small" onClick={load} data-testid="rg-fin-refresh" disabled={loading}>
          <RefreshCw size={13} /> Actualiser
        </button>
      </div>

      {/* Jauge objectif */}
      <div className="rg-card" data-testid="rg-fin-gauge" style={{ padding: 16, marginBottom: 16 }}>
        <div className="rg-row" style={{ justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 6 }}>
          <div className="rg-row" style={{ gap: 6, alignItems: "center" }}>
            <TrendingUp size={16} style={{ color: "var(--rg-text-2)" }} />
            <span style={{ fontWeight: 600 }}>Objectif de trésorerie du mois</span>
          </div>
          <div data-testid="rg-fin-gauge-value" style={{ fontWeight: 700, fontSize: 18, color: reached ? "#2f7a43" : "var(--rg-text)" }}>
            {eur(gauge.total)} <span className="rg-muted" style={{ fontWeight: 400, fontSize: 13 }}>/ {eur(CASH_TARGET)}</span>
          </div>
        </div>
        <div style={{ marginTop: 10, height: 14, borderRadius: 999, background: "var(--rg-border-soft)", overflow: "hidden" }}>
          <div data-testid="rg-fin-gauge-bar" style={{ height: "100%", width: `${pct}%`, background: reached ? "linear-gradient(90deg,#4caf7d,#2f7a43)" : "linear-gradient(90deg,#c9a84c,#A8956F)", transition: "width 400ms ease" }} />
        </div>
        <div className="rg-row" style={{ justifyContent: "space-between", marginTop: 6 }}>
          <span className="rg-tiny" style={{ color: "var(--rg-text-3)" }}>{Math.round(pct)}% · {gauge.nightsCount} nuit(s) + {gauge.expCount} expérience(s)</span>
          <span className="rg-tiny" style={{ color: reached ? "#2f7a43" : "var(--rg-text-3)" }}>{reached ? "Objectif atteint 🎉" : `Reste ${eur(Math.max(0, CASH_TARGET - gauge.total))}`}</span>
        </div>
      </div>

      {/* En-têtes jours */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 6, marginBottom: 6 }}>
        {WEEKDAYS.map((w) => (
          <div key={w} className="rg-tiny" style={{ textAlign: "center", fontWeight: 700, color: "var(--rg-text-3)", textTransform: "uppercase", letterSpacing: 1 }}>{w}</div>
        ))}
      </div>

      {/* Grille calendrier financier */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 6, alignItems: "stretch" }} data-testid="rg-fin-grid">
        {cells.map((day, idx) => {
          if (day === null) return <div key={`b${idx}`} />;
          const iso = toIso(new Date(year, month, day));
          const cell = dayMap[iso] || { nights: [], departures: [], exps: [] };
          const free = cell.nights.length === 0;
          return (
            <div key={iso} data-testid={`rg-fin-cell-${iso}`}
              style={{
                border: "1px solid var(--rg-border)", borderRadius: 8, padding: 6, minHeight: 112,
                background: free ? "var(--rg-bg)" : "#fff", display: "flex", flexDirection: "column", gap: 4,
              }}>
              <div className="rg-row" style={{ justifyContent: "space-between", alignItems: "center" }}>
                <span className="rg-tiny" style={{ fontWeight: 700, color: "var(--rg-text-2)" }}>{day}</span>
                {free && <span className="rg-tiny" style={{ color: "var(--rg-text-3)", fontStyle: "italic" }}>libre</span>}
              </div>

              {/* Nuits réservées (montant par nuit répété) */}
              {cell.nights.map(({ r, platform }, i) => {
                const rt = RATE[platform];
                const isA = platform === "airbnb";
                const showBand = !!rt && firstNightByResa[r.id] === iso;
                return (
                  <div key={`n${i}`} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <button onClick={() => nav(`/admin/gestion/voyageur/${r.id}`)}
                      data-testid={`rg-fin-night-${iso}-${r.id}`}
                      style={{
                        textAlign: "left", border: "none", cursor: "pointer", borderRadius: 6, padding: 6, width: "100%",
                        background: isA ? "rgba(255,56,92,0.06)" : "rgba(5,4,4,0.05)",
                        borderLeft: `3px solid ${isA ? "#FF385C" : "#050404"}`,
                      }}>
                      <div className="rg-row" style={{ justifyContent: "space-between", alignItems: "center", gap: 4 }}>
                        <span style={{ fontSize: 10, fontWeight: 800, color: isA ? "#FF385C" : "#111", textTransform: "none" }}>
                          {isA ? "Airbnb" : "Lodgify"}
                        </span>
                        <span style={{ fontSize: 10, color: "var(--rg-text-3)" }}>{firstName(r.name)}</span>
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.2, color: "var(--rg-text)" }}>
                        {rt ? eur(rt.big) : "à renseigner"}
                      </div>
                      {rt && <div style={{ fontSize: 9, lineHeight: 1.3, color: "var(--rg-text-3)" }}>Valeur nuitée : {eur0(rt.nightValue)}</div>}
                    </button>

                    {/* Bandeau unique du séjour (Airbnb & Lodgify) — informatif, hors jauge, non répété */}
                    {showBand && rt && (
                      <div data-testid={`rg-fin-stayband-${r.id}`}
                        style={{ fontSize: 9, lineHeight: 1.4, background: isA ? "rgba(255,56,92,0.08)" : "rgba(5,4,4,0.05)", border: `1px solid ${isA ? "rgba(255,56,92,0.25)" : "rgba(5,4,4,0.2)"}`, borderRadius: 6, padding: "5px 7px", color: "var(--rg-text-2)" }}>
                        <div style={{ fontWeight: 700 }}>Séjour {ddmm(r.date_arrival)}→{ddmm(r.date_departure)} · {r.nights} nuit{r.nights > 1 ? "s" : ""}</div>
                        {rt.band.map(([label, per], bi) => (
                          <div key={bi}>{label} : {bi === 0 ? <b>{eur(per * (r.nights || 0))}</b> : eur(per * (r.nights || 0))}</div>
                        ))}
                        <div style={{ fontStyle: "italic", color: "var(--rg-text-3)" }}>informatif · hors jauge</div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Expériences du jour (comptées une fois) */}
              {cell.exps.map((x, i) => (
                <div key={`e${i}`} data-testid={`rg-fin-exp-${iso}-${x.key}`}
                  style={{ fontSize: 10, fontWeight: 600, background: "rgba(168,149,111,0.14)", borderRadius: 6, padding: "3px 6px" }}>
                  {EXP[x.key].icon} {EXP[x.key].label} · {eur0(EXP[x.key].price)}
                </div>
              ))}

            </div>
          );
        })}
      </div>

      <p className="rg-tiny" style={{ color: "var(--rg-text-3)", marginTop: 12, lineHeight: 1.5 }}>
        Montant en gros = net compté dans la jauge (Airbnb 134,35 € · Lodgify 163,55 €, taxe non redéduite). Détails prévisionnels en petit.
        Expériences au jour prévu, 1 fois : 🥐 50 € · 🌸 70 € · 🌹 50 €. Marchés et ménage exclus.
      </p>
    </div>
  );
}
